import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Image, Stack } from "react-bootstrap";

import "./DownloadAndroidAppModal.scss";
import { GenericModalProps } from "../../utils/constants/generic-types";
import InfoIcon2 from "../icons/InfoIcon2";
import useDisclosure from "../../hooks/useDisclosure";
import BottomDrawer from "../BottomDrawer/BottomDrawer";
import PrimaryActionButton from "../Buttons/PrimaryActionButton/PrimaryActionButton";
import ImageDownloadAnyway from "../../images/download-anyway.svg";
import ImageSSL from "../../images/ssl-image.png";
import ImageFacePile from "../../images/face-pile.png";
import ImageAndroidIcon from "../../images/android-icon.svg";
import ImageDownloadLocation from "../../images/download-tutorial.gif";
import CustomModal from "../CustomModal/CustomModal";
import { useDownloadApp } from "../../hooks/useDownloadApp";
import { showSuccessToast } from "../../utils/show-toast";
import BackButton from "../Buttons/BackButton/BackButton";
import useAuth from "../../hooks/useAuth";
import { ROUTES } from "../../utils/constants";

enum DOWNLOAD_PHASE {
  BEFORE_DOWNLOAD = "before",
  AFTER_DOWNLOAD = "after",
}

interface BeforeDownloadProps {
  openInfoDialog: () => void;
  changeDownloadPhase: () => void;
}

function WhyWeUseAnApk({ isOpen, onClose }: GenericModalProps) {
  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      containerClass="WhyWeUseAnApk"
      backdropClassName="WhyWeUseAnApkBackdrop"
    >
      <Stack gap={4} className="mt-3 text-center">
        <Stack gap={2}>
          <h2 className="fs-6 Hanson-700 SpecialFontSettings DialogTitle">
            Here&apos;s why we use an APK
          </h2>
          <Stack gap={4} className="Grotesk SpecialFontSettings Explanation">
            <p>APKs are apps that are not on the Google Play Store.</p>
            <p>
              Apps that allow gamers win real money from games of skill are
              heavily scrutinized by Google. Only gambling apps (games of
              chance) are allowed by Google. Apple App Store allows our app.
            </p>
          </Stack>
        </Stack>
        <PrimaryActionButton text="Okay" onClick={onClose} />
      </Stack>
    </BottomDrawer>
  );
}

function BeforeDownload({
  openInfoDialog,
  changeDownloadPhase,
  closeModal,
}: BeforeDownloadProps & {
  closeModal: GenericModalProps["onClose"];
}) {
  const { apk } = useDownloadApp();

  return (
    <>
      <div className="text-center">
        <div>
          <BackButton
            style={{ position: "static", marginBottom: 16 }}
            onClick={closeModal}
          />
        </div>
        <p className="fw-bold text-center Grotesk SpecialFontSettings HyperAsApkInfoText">
          Hyper is available only as an Android APK due to real money gaming
          rewards.
          <button
            aria-label="Open info dialog"
            onClick={openInfoDialog}
            className="p-0 d-inline-flex InfoButton"
          >
            <InfoIcon2 fill="#6C53E8" />
          </button>
        </p>
        <p className="Grotesk SpecialFontSettings DownloadAnywayText">
          Tap <span className="fw-bold">"Download anyway"</span> when you see
          this pop up 👇
        </p>
        <Image
          src={ImageDownloadAnyway}
          alt="Android 'Files might be harmful' download notification"
          width={260}
          height={140}
          className="DownloadAnywayImage"
        />
        <Image
          src={ImageSSL}
          alt=""
          width={127}
          height={42}
          className="SslImage"
        />
      </div>

      <div className="w-100">
        <Stack gap={3} className="align-items-center TrustedBy">
          <p className="text-capitalize fs-6">
            Trusted by over 60 thousand users
          </p>
          <Image
            src={ImageFacePile}
            alt="Avatar pile"
            width={134}
            height={41}
          />
        </Stack>
        <PrimaryActionButton
          href={apk.url}
          text="Download Secure APK"
          leftImage={ImageAndroidIcon}
          imgClassName="AndroidIcon"
          buttonClassName="DownloadCta"
          onClick={changeDownloadPhase}
        />
      </div>
    </>
  );
}

function AfterDownload({
  closeModal,
}: {
  closeModal: GenericModalProps["onClose"];
}) {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    if (isLoggedIn) {
      closeModal();
    } else {
      navigate(ROUTES.ROOT.LOG_IN);
    }
  }, [closeModal, isLoggedIn, navigate]);

  return (
    <>
      <div className="text-center">
        <div>
          <BackButton
            style={{ position: "static", marginBottom: 16 }}
            onClick={closeModal}
          />
        </div>
        <p className="fw-bold text-center text-capitalize Grotesk HyperAppIsDownloading">
          Hyper App is Downloading...
        </p>
        <ul className="d-flex flex-column DownloadInstructions">
          <li className="d-flex align-items-center DownloadInstruction">
            <span className="d-inline-block fw-bold Grotesk InstructionCount">
              1
            </span>
            <span className="text-start Grotesk InstructionText">
              Wait for download to complete
            </span>
          </li>
          <li className="d-flex align-items-center DownloadInstruction">
            <span className="d-inline-block fw-bold Grotesk InstructionCount">
              2
            </span>
            <span className="text-start Grotesk InstructionText">
              Open download folder and tap the file to install Hyper
            </span>
          </li>
          {!isLoggedIn && (
            <li className="d-flex align-items-center DownloadInstruction">
              <span className="d-inline-block fw-bold Grotesk InstructionCount">
                3
              </span>
              <span className="text-start Grotesk InstructionText">
                Continue to sign up while Hyper app is downloading
              </span>
            </li>
          )}
        </ul>
        <div className="DownloadLocationImageContainer">
          <Image
            src={ImageDownloadLocation}
            alt="Show app download location"
            width={288.5}
            height={166}
          />
        </div>
      </div>
      <div className="w-100">
        <PrimaryActionButton
          text={isLoggedIn ? "Continue" : "Continue to sign up"}
          buttonClassName="w-100 LoginCta"
          onClick={handleClick}
        />
      </div>
    </>
  );
}

export default function DownloadAndroidAppModal({
  isOpen,
  onClose,
}: GenericModalProps) {
  const [downloadPhase, setDownloadPhase] = useState<DOWNLOAD_PHASE>(
    DOWNLOAD_PHASE.BEFORE_DOWNLOAD
  );
  const {
    isOpen: isInfoDialogOpen,
    onOpen: openInfoDialog,
    onClose: closeInfoDialog,
  } = useDisclosure();

  const changeDownloadPhase = (newPhase: DOWNLOAD_PHASE) => {
    setDownloadPhase(newPhase);
  };

  function handleStartDownload() {
    showSuccessToast("Download Started");
    changeDownloadPhase(DOWNLOAD_PHASE.AFTER_DOWNLOAD);
  }

  const handleCloseModal = () => {
    onClose();
    setTimeout(() => changeDownloadPhase(DOWNLOAD_PHASE.BEFORE_DOWNLOAD), 1000);
  };

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleCloseModal}
        isFullscreen
        showCloseBtn={false}
      >
        <div className="h-100 d-flex flex-column align-items-center justify-content-between DownloadAndroidAppModal">
          {downloadPhase === DOWNLOAD_PHASE.BEFORE_DOWNLOAD && (
            <BeforeDownload
              openInfoDialog={openInfoDialog}
              changeDownloadPhase={handleStartDownload}
              closeModal={handleCloseModal}
            />
          )}
          {downloadPhase === DOWNLOAD_PHASE.AFTER_DOWNLOAD && (
            <AfterDownload closeModal={handleCloseModal} />
          )}
        </div>
      </CustomModal>

      <WhyWeUseAnApk isOpen={isInfoDialogOpen} onClose={closeInfoDialog} />
    </>
  );
}
